// `capture_sessions` PATCH dispatch — extracted from connector.ts so
// that file stays under the 500-line gate as more mutation kinds
// migrate to the local-first PowerSync write path.
//
// Used by completeSession and reopenSession from the PowerSync adapter:
// both write to the SAME row with the SAME columns; the only signal of
// intent is the prior value of completed_at (tracked by the schema's
// `trackPrevious` option).
//
//   prev null     → now non-null    => PUT /capture/sessions/:id/complete
//   prev non-null → now null        => PUT /capture/sessions/:id/reopen
//   no completion-state change      => no-op ACK (drain) — see the same-state
//                                       branch below; this used to throw, which
//                                       wedged the whole queue (CITYCARE-ASSET-
//                                       CAPTURE-1S). The adapter no longer emits
//                                       these; the ack recovers pre-fix devices.
//
// Body shape `{ syncVersion }` matches the existing REST endpoints
// exactly; sessionId is in the URL.
//
// RE-HOME redirect: if this session's INSERT collided (409 already_exists) and
// was re-homed onto an authoritative server session, the local orphan id the
// SDK froze into THIS PATCH is gone server-side. We redirect the toggle onto the
// authoritative session id (connector-session-rehome-registry.ts) so the
// surveyor's offline "complete" lands instead of 404-ing. Mirrors the
// capture_items re-home redirect.
//
// 404 NOT_FOUND: the session genuinely doesn't exist server-side and wasn't
// re-homed (or the authoritative is also gone), so the complete/reopen can NEVER
// land. Throwing would wedge the ENTIRE upload queue (uploadData acks a batch
// only once every entry succeeds — every later mutation for every table blocks
// behind this one). We ACK so the queue drains; the building's status reconciles
// from whatever live session exists. We LOG it (Sentry + console) so the dropped
// completion intent is visible, not silent.
//
// 409 FAILED_PRECONDITION: another client toggled first, OR a stale sync_version.
// Unlike capture_items, the /complete + /reopen endpoints don't return the
// authoritative row, so we can't reapply at the right version here. We swallow
// so the queue drains and the next sync delivers the authoritative completed_at
// — but LOG it (info) so a swallowed completion is visible rather than silent (a
// stale-version 409 could otherwise lose a genuine toggle without a trace).

import type { CrudEntry } from '@powersync/web'
import * as Sentry from '@sentry/react'

import { ApiError, apiFetch, isNotFoundError } from '../api'

import { getSessionRehome } from './connector-session-rehome-registry'
import { _emitUploaded } from './upload-events'

export async function dispatchSessionPatch(entry: CrudEntry): Promise<void> {
  const opData = entry.opData ?? {}
  const previousValues = entry.previousValues ?? {}
  // `!= null` deliberately treats an ABSENT completed_at as "not completed", so a
  // same-state PATCH that bumped only sync_version (the SDK's `onlyWhenChanged`
  // drops the unchanged completed_at from BOTH opData and previousValues) reads as
  // wasCompleted === isCompleted === false → the same-completion-state else branch
  // → no-op ack, not a spurious /reopen. Absent == no toggle (audit-1 #6).
  const wasCompleted = previousValues['completed_at'] != null
  const isCompleted = opData['completed_at'] != null

  // Redirect onto the authoritative session if this session's INSERT was
  // re-homed earlier (in this batch, a prior one, or before an app reload — the
  // registry is durable). The local orphan id is deleted server-side, so PUTing
  // it would 404; the authoritative id is the live session the children were
  // re-homed onto, and rehome.syncVersion is its server version (used for the
  // body below so the redirected toggle doesn't 412). See the file header.
  const rehome = getSessionRehome(entry.id)
  const sessionId = rehome?.authoritativeId ?? entry.id

  let transition: 'complete' | 'reopen'
  if (!wasCompleted && isCompleted) {
    transition = 'complete'
  } else if (wasCompleted && !isCompleted) {
    transition = 'reopen'
  } else {
    // SAME COMPLETION STATE — no toggle (completed_at non-null → non-null, or
    // null → null). The adapter no longer generates these (capture-client-
    // powersync.ts now gates its UPDATE on an actual transition), but a device
    // that queued one BEFORE that fix shipped would wedge the ENTIRE upload queue
    // forever on a throw here: uploadData rethrows it (it's a plain Error, not a
    // permanent ApiError, so isPermanentUploadError can't drain it), batch.complete
    // never runs, and every count behind it is blocked while the UI shows "saved".
    // That is the production wedge: Sentry CITYCARE-ASSET-CAPTURE-1S (2026-06-29).
    //
    // ACK it (drain) instead. There is NO server action to take — the session's
    // completion state already matches what the client wants — and a session row
    // carries no count of its own (its child capture_items each upload their own
    // entries), so draining loses nothing. LOG (info, not silent) so an
    // unexpected same-state PATCH stays visible, mirroring the not_found /
    // failed_precondition ack-to-avoid-a-wedge logging below.
    Sentry.captureMessage(
      '[powersync] capture_sessions PATCH with no completion-state change — acking as a no-op to drain (the same-state wedge)',
      {
        level: 'info',
        extra: {
          reason: 'powersync_session_patch_noop_samestate_ack',
          entryId: entry.id,
          sessionId,
        },
      },
    )
    return
  }
  const path = `/capture/sessions/${encodeURIComponent(sessionId)}/${transition}`

  // Pick the syncVersion the backend's applyVersionGuard will validate against.
  //
  // REDIRECTED (re-homed) toggle: the orphan's own sync_version is meaningless
  // against the authoritative row, so sending it would 412 failed_precondition
  // and the swallow path below would silently lose the surveyor's complete/reopen
  // (Codex on PR 660). Use the authoritative syncVersion recorded with the
  // re-home (null → fall back to the orphan version, same as a non-redirect).
  //
  // NORMAL toggle: send the PRE-BUMP syncVersion. The adapter writes an optimistic
  // N+1 to the local row, but the backend validates against the caller's
  // pre-update version (N), carried here via the schema's
  // `trackPrevious: { columns: [..., 'sync_version'] }`. Without this every toggle
  // 409s on upload and the swallow path acks the failed batch — the toggle never
  // reaches the backend (Codex P1 on the first cut of this path). Defensive
  // fallback when previousValues lacks sync_version: opData.sync_version - 1.
  const opSync = opData['sync_version']
  const prevSync = previousValues['sync_version']
  const callerSyncVersion =
    rehome?.syncVersion != null
      ? rehome.syncVersion
      : typeof prevSync === 'number'
        ? prevSync
        : typeof opSync === 'number'
          ? opSync - 1
          : undefined

  try {
    await apiFetch(path, {
      method: 'PUT',
      json: { syncVersion: callerSyncVersion },
    })
    _emitUploaded(entry.table, entry.id)
  } catch (err) {
    if (err instanceof ApiError && err.code === 'failed_precondition') {
      // 409 — swallow so the queue drains; the next sync delivers the
      // authoritative completed_at. LOG (not silent) so a stale-version 409
      // that loses a genuine toggle is at least visible. See the file header.
      Sentry.captureMessage(
        '[powersync] capture_sessions PATCH failed_precondition — swallowed; next sync reconciles',
        {
          level: 'info',
          extra: {
            reason: 'powersync_session_patch_precondition_swallow',
            entryId: entry.id,
            sessionId,
            transition,
          },
        },
      )
      return
    }
    if (isNotFoundError(err)) {
      // 404 — the session is gone server-side and wasn't re-homed, so the
      // toggle can NEVER land. Throwing would wedge the ENTIRE upload queue, so
      // ACK to drain it (the building status reconciles from the live session).
      // LOG so the dropped completion intent is visible. See the file header.
      // isNotFoundError covers a code-less 404 too (a proxy/CDN 404 with no
      // Encore code — H3/#694), so it acks inline here with this captureMessage
      // rather than falling to the backstop's captureException.
      Sentry.captureMessage(
        '[powersync] capture_sessions PATCH not_found — session gone server-side; acking to avoid a queue wedge',
        {
          level: 'warning',
          extra: {
            reason: 'powersync_session_patch_not_found_ack',
            entryId: entry.id,
            sessionId,
            transition,
          },
        },
      )
      console.warn(
        '[powersync] capture_sessions PATCH not_found — session gone, acking (no wedge)',
        { id: entry.id, sessionId, transition },
      )
      return
    }
    throw err
  }
}
